import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, addDays, subDays } from 'date-fns';
import { ko } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Check, X, Loader2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { passwordsApi, RoomWithInfo, PasswordChange } from '../api/passwords';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './PasswordChangePage.css';

// 영업일 계산 (새벽 2시 기준)
function getBusinessDate(): Date {
  const now = new Date();
  if (now.getHours() < 2) {
    return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
  return now;
}

// 최근 변경 배지 색상
function getDaysBadgeClass(days: number | null): string {
  if (days === null) return 'badge-secondary';
  if (days === 0) return 'badge-primary';
  if (days <= 60) return 'badge-success';
  if (days <= 120) return 'badge-warning';
  if (days <= 180) return 'badge-danger';
  return 'badge-dark';
}

// 상태 배지
function getRoomStatusBadge(room: RoomWithInfo | PasswordChange) {
  if (room.isStaying) {
    return <span className="badge badge-info">연박중</span>;
  }
  if (room.todayCheckoutTime && !room.todayCheckinTime) {
    return <span className="badge badge-success">체크아웃만</span>;
  }
  if (!room.todayCheckoutTime && room.todayCheckinTime) {
    return <span className="badge badge-primary">체크인만</span>;
  }
  if (room.todayCheckoutTime && room.todayCheckinTime) {
    return <span className="badge badge-warning">체크아웃/체크인</span>;
  }
  return <span className="badge badge-secondary">공실</span>;
}

export default function PasswordChangePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentDate, setCurrentDate] = useState(getBusinessDate());
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());
  const [newPasswords, setNewPasswords] = useState<Record<number, string>>({});

  const dateStr = format(currentDate, 'yyyy-MM-dd');

  // 사용 가능한 객실 목록 조회
  const { data: availableRooms, isLoading: isLoadingRooms } = useQuery({
    queryKey: ['passwordRooms', dateStr],
    queryFn: () => passwordsApi.getAvailableRooms(dateStr),
  });

  // 선택된 객실 목록 조회
  const { data: pendingChanges, isLoading: isLoadingChanges } = useQuery({
    queryKey: ['passwordChanges', dateStr],
    queryFn: () => passwordsApi.getPendingChanges(dateStr),
  });

  // 객실 선택 mutation
  const selectMutation = useMutation({
    mutationFn: (roomIds: string[]) => passwordsApi.selectRooms(roomIds, dateStr),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwordRooms'] });
      queryClient.invalidateQueries({ queryKey: ['passwordChanges'] });
      setSelectedRoomIds(new Set());
    },
  });

  // 비밀번호 예약 mutation
  const reserveMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      passwordsApi.reservePassword(id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwordChanges'] });
      setNewPasswords({});
    },
  });

  // 비밀번호 적용 mutation
  const applyMutation = useMutation({
    mutationFn: (id: number) => passwordsApi.applyPassword(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwordRooms'] });
      queryClient.invalidateQueries({ queryKey: ['passwordChanges'] });
    },
  });

  // 취소 mutation
  const cancelMutation = useMutation({
    mutationFn: (id: number) => passwordsApi.cancelChange(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['passwordRooms'] });
      queryClient.invalidateQueries({ queryKey: ['passwordChanges'] });
    },
  });

  const handleRoomSelect = (roomId: string) => {
    const newSet = new Set(selectedRoomIds);
    if (newSet.has(roomId)) {
      newSet.delete(roomId);
    } else {
      newSet.add(roomId);
    }
    setSelectedRoomIds(newSet);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && availableRooms) {
      const selectableIds = availableRooms
        .filter(r => !r.isAlreadySelected)
        .map(r => r.roomId);
      setSelectedRoomIds(new Set(selectableIds));
    } else {
      setSelectedRoomIds(new Set());
    }
  };

  const handleAddRooms = () => {
    if (selectedRoomIds.size > 0) {
      selectMutation.mutate(Array.from(selectedRoomIds));
    }
  };

  const handleReservePassword = (id: number) => {
    const password = newPasswords[id];
    if (password) {
      reserveMutation.mutate({ id, password });
    }
  };

  const handleApplyPassword = (id: number) => {
    if (confirm('비밀번호를 변경하시겠습니까?')) {
      applyMutation.mutate(id);
    }
  };

  const handleCancelChange = (id: number) => {
    if (confirm('취소하시겠습니까?')) {
      cancelMutation.mutate(id);
    }
  };

  const selectableRooms = availableRooms?.filter(r => !r.isAlreadySelected) || [];
  const allSelected = selectableRooms.length > 0 && selectedRoomIds.size === selectableRooms.length;

  return (
    <div className="password-change-shell">
      {/* Header */}
      <div className="password-change-header">
        <h2 className="password-change-title">객실 비밀번호 관리</h2>
        <div className="password-change-nav">
          <button
            className="btn btn-icon"
            onClick={() => setCurrentDate(d => subDays(d, 1))}
          >
            <ChevronLeft size={20} />
          </button>
          <DatePicker
            selected={currentDate}
            onChange={(date) => date && setCurrentDate(date)}
            dateFormat="yyyy-MM-dd"
            locale={ko}
            customInput={
              <button className="btn btn-date">
                <CalendarIcon size={16} />
                <span>{format(currentDate, 'yyyy년 M월 d일', { locale: ko })}</span>
              </button>
            }
          />
          <button
            className="btn btn-icon"
            onClick={() => setCurrentDate(d => addDays(d, 1))}
          >
            <ChevronRight size={20} />
          </button>
          <button
            className="btn btn-today"
            onClick={() => setCurrentDate(getBusinessDate())}
          >
            오늘
          </button>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/?date=${dateStr}`)}
        >
          대시보드
        </button>
      </div>

      {/* Available Rooms Section */}
      <div className="card available-rooms-card">
        <div className="card-header card-header-primary">
          <h4>사용 가능한 객실 ({availableRooms?.length || 0}개)</h4>
        </div>
        <div className="card-body">
          {isLoadingRooms ? (
            <LoadingSpinner className="py-8" />
          ) : (
            <>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th>객실</th>
                      <th>최근 변경</th>
                      <th>체크아웃</th>
                      <th>체크인</th>
                      <th>상태</th>
                      <th>현재 비밀번호</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableRooms?.map(room => (
                      <tr
                        key={room.roomId}
                        className={`room-row ${room.isAlreadySelected ? 'disabled' : ''}`}
                        onClick={() => !room.isAlreadySelected && handleRoomSelect(room.roomId)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedRoomIds.has(room.roomId)}
                            onChange={() => handleRoomSelect(room.roomId)}
                            disabled={room.isAlreadySelected}
                          />
                        </td>
                        <td><strong>{room.roomAddress.toUpperCase()}</strong></td>
                        <td>
                          {room.daysSinceChange !== null ? (
                            <span className={`badge ${getDaysBadgeClass(room.daysSinceChange)}`}>
                              {room.daysSinceChange === 0 ? '오늘' : `${room.daysSinceChange}일 전`}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {room.todayCheckoutTime || '-'}
                          {room.todayLateCheckout && room.todayLateCheckoutTime && (
                            <span className="badge badge-warning" style={{ marginLeft: 4 }}>
                              Late: {room.todayLateCheckoutTime}
                            </span>
                          )}
                        </td>
                        <td>{room.todayCheckinTime || '-'}</td>
                        <td>{getRoomStatusBadge(room)}</td>
                        <td><code>{room.password || '-'}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3">
                <button
                  className="btn btn-primary"
                  onClick={handleAddRooms}
                  disabled={selectedRoomIds.size === 0 || selectMutation.isPending}
                >
                  {selectMutation.isPending ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <>
                      <Plus size={16} />
                      선택한 객실 추가
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Selected Rooms Section */}
      <div className="card selected-rooms-card">
        <div className="card-header card-header-warning">
          <h4>비밀번호 변경 대상 객실</h4>
        </div>
        <div className="card-body">
          {isLoadingChanges ? (
            <LoadingSpinner className="py-8" />
          ) : pendingChanges && pendingChanges.length > 0 ? (
            <div className="password-cards">
              {pendingChanges.map(change => (
                <div
                  key={change.id}
                  className={`password-card status-${change.status}`}
                >
                  <div className="password-card-header">
                    <h5>{change.roomAddress.toUpperCase()}</h5>
                    {change.status === 'reserved' ? (
                      <span className="badge badge-info">예약됨</span>
                    ) : (
                      <span className="badge badge-warning">대기중</span>
                    )}
                  </div>

                  <div className="password-card-body">
                    <div className="info-row">
                      <span className="label">선택 날짜:</span>
                      <strong>{format(new Date(change.selectedDate), 'yyyy-MM-dd')}</strong>
                    </div>

                    <div className="info-row">
                      <span className="label">최근 변경:</span>
                      {change.daysSinceChange !== null ? (
                        <span className={`badge ${getDaysBadgeClass(change.daysSinceChange)}`}>
                          {change.daysSinceChange === 0 ? '오늘' : `${change.daysSinceChange}일 전`}
                        </span>
                      ) : (
                        <span className="badge badge-secondary">변경 이력 없음</span>
                      )}
                    </div>

                    <div className="info-row">
                      <span className="label">체크아웃:</span>
                      <strong>
                        {change.todayCheckoutTime || '-'}
                        {change.todayLateCheckout && change.todayLateCheckoutTime && (
                          <span className="badge badge-warning" style={{ marginLeft: 4 }}>
                            Late: {change.todayLateCheckoutTime}
                          </span>
                        )}
                      </strong>
                    </div>

                    <div className="info-row">
                      <span className="label">체크인:</span>
                      <strong>{change.todayCheckinTime || '-'}</strong>
                    </div>

                    <div className="info-row">
                      <span className="label">상태:</span>
                      {getRoomStatusBadge(change)}
                    </div>

                    <div className="info-row">
                      <span className="label">현재 비밀번호:</span>
                      <strong><code>{change.currentPassword || '-'}</code></strong>
                    </div>

                    {change.status === 'pending' ? (
                      <div className="password-input-group">
                        <input
                          type="text"
                          placeholder="새 비밀번호"
                          value={newPasswords[change.id] || ''}
                          onChange={(e) => setNewPasswords(prev => ({
                            ...prev,
                            [change.id]: e.target.value
                          }))}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleReservePassword(change.id)}
                          disabled={!newPasswords[change.id] || reserveMutation.isPending}
                        >
                          예약
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="info-row new-password">
                          <span className="label">변경될 비밀번호:</span>
                          <strong className="text-primary"><code>{change.newPassword}</code></strong>
                        </div>
                        <div className="action-buttons">
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleApplyPassword(change.id)}
                            disabled={applyMutation.isPending}
                          >
                            <Check size={14} /> 적용
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleCancelChange(change.id)}
                            disabled={cancelMutation.isPending}
                          >
                            <X size={14} /> 취소
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted">선택된 객실이 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
}
